import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import RRDJoinConfirmLocationCard from "./RRDJoinConfirmLocationCard";
import RRDJoinConfirmTotalCard from "./RRDJoinConfirmTotalCard";
import ConfirmStripeEmbedCheckoutPopup from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { rrdCampaignService } from "@/features/campaigns/direct-mail/rrd/services/rrdCampaignService";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS } from "@/config/constants";
import { Button, Grid, Typography } from "@mui/material";
import { DIRECT_MAIL_CAMP_PROVIDER } from "@/features/campaigns/direct-mail/utils/directMailConstants";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

const GridItem = Grid as React.ElementType;
const StripeCheckoutPopup =
  ConfirmStripeEmbedCheckoutPopup as React.ElementType;

type RRDConfirmationSelection = Record<string, any>;
type RRDJoinConfirmationProps = {
  campaign?: Record<string, any>;
};

export function adaptRRDConfirmationSelection(
  selection?: RRDConfirmationSelection | null
) {
  if (!selection) return selection;

  const { locationInfo } = selection;
  const layoutKey = locationInfo?.layout_key || selection.layout_key || null;
  const fixedImage = locationInfo?.fixed_image;
  const printType = selection.printType || locationInfo?.print_type;
  const printTypeLabel = locationInfo?.print_type_display;

  const normalized: RRDConfirmationSelection = {
    ...selection,
    layout_key: layoutKey,
  };

  if (fixedImage) {
    normalized.fixedImages = [fixedImage];
  }
  if (printType) normalized.printType = printType;
  if (printTypeLabel) normalized.printTypeLabel = printTypeLabel;

  return normalized;
}

function RRDJoinConfirmation({ campaign }: RRDJoinConfirmationProps) {
  const navigate = useNavigate();
  const { loggedInUser, selectedBrand } = useContext(GlobalContext) as Record<
    string,
    any
  >;
  const loader = useContext(LoaderContext) as (_loading: boolean) => void;

  const [openPayment, setOpenPayment] = useState(false);
  const [loading, setLoading] = useState(false);

  const selections = campaign?.selections;
  const totalBudget = campaign?.totalBudget;

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  useEffect(() => {
    return () => {
      loader(false);
    };
  }, [loader]);

  const dataToSave = useCallback(
    (payment_status: number = 0) => {
      const payload = {
        orderIds: campaign?.orderIds,
        locationData: campaign?.locationData,
        payment_status,
        total_budget: totalBudget,
        campaign_cat: "Postcard",
        provider: DIRECT_MAIL_CAMP_PROVIDER.RRD,

        id: campaign?.campaignId,
        fk_campaign_id: campaign?.campaignId,
        fk_campaign_name: campaign?.campaignName,
        user_id: loggedInUser?.id,
        user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
        system_generated_participant_id:
          loggedInUser?.participantData?.system_generated_participant_id,
        fk_participant_id: loggedInUser?.participantData?.id,
        fk_brand_id: selectedBrand?.id,
        system_generated_brand_id: selectedBrand?.system_generated_brand_id,
        fk_brand_name: selectedBrand?.brand_name,
        is_white_label: selectedBrand?.is_white_label,
      };
      return payload;
    },
    [
      campaign?.orderIds,
      campaign?.locationData,
      totalBudget,
      campaign?.campaignId,
      campaign?.campaignName,
      loggedInUser,
      selectedBrand,
    ]
  );

  const handleOnFinalConfirm = useCallback(
    async (_paymentStatus?: number) => {
      let navigatedToThankYou = false;
      try {
        setLoading(true);
        const res =
          await rrdCampaignService.confirmRRDCampaignJoin(dataToSave());
        if (res.status === HTTP_STATUS.OK) {
          navigatedToThankYou = true;
          setLoading(false);
          loader(false);
          grantThankYouAccessForFlow({
            stateFrom: "rrd-join-campaign-confirm",
          });
          navigate(ROUTES.THANK_YOU, {
            replace: true,
            state: {
              from: "rrd-join-campaign-confirm",
              campaignCat: "directmail",
              provider: DIRECT_MAIL_CAMP_PROVIDER.RRD,
            },
          });
        }
      } catch (err) {
        logger.error("Error in handleOnFinalConfirm", { err });
      } finally {
        if (!navigatedToThankYou) {
          setLoading(false);
        }
      }
    },
    [dataToSave, loader, navigate]
  );

  const onMakeChanges = useCallback(() => {
    navigate(`/participant/join-postcard-campaign/${campaign?.campaignId}`, {
      replace: true,
    });
  }, [campaign?.campaignId, navigate]);

  return (
    <div>
      {selections?.map((selection: RRDConfirmationSelection, index: number) => (
        <RRDJoinConfirmLocationCard
          key={selection.orderId || selection.locationId || index}
          location={adaptRRDConfirmationSelection(selection)}
        />
      ))}
      <RRDJoinConfirmTotalCard
        totalBudget={totalBudget}
        text="Total You Pay:"
        textColor="primary"
      />

      <Grid container spacing={2} className="mt-5">
        <GridItem item size={{ md: "grow" }}>
          <Typography variant="caption" className="text-right">
            Once 'Confirm' is selected, you can't make changes, your selections
            will be final for this campaign
          </Typography>
        </GridItem>
        <GridItem item size={{ xs: 12, md: "auto" }}>
          <Grid container spacing={2}>
            <GridItem item size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                color="primary"
                className="px-2"
                onClick={onMakeChanges}
                fullWidth
              >
                Make Changes
              </Button>
            </GridItem>
            <GridItem item size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                color="primary"
                className="px-5"
                fullWidth
                onClick={() => {
                  if (campaign?.paymentMethod?.toLowerCase() === "invoice") {
                    handleOnFinalConfirm(0);
                  } else {
                    setOpenPayment(true);
                  }
                }}
              >
                Confirm
              </Button>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>

      {openPayment ? (
        <StripeCheckoutPopup
          makePayment={openPayment}
          title="Payment"
          data={{
            email: loggedInUser?.email,
            amount: totalBudget,
            fk_brand_id: selectedBrand?.id,
            system_generated_brand_id: selectedBrand?.system_generated_brand_id,
            fk_brand_name: selectedBrand?.brand_name,
            ...dataToSave(1),
          }}
          onClose={() => setOpenPayment(false)}
          return_uri={`/rrd-join-campaign-return?join_campaign=true`}
          api={stripeMicroService.joinCampaignPayment}
        />
      ) : null}
    </div>
  );
}

export default RRDJoinConfirmation;
